import { useId } from "react";

/**
 * The house empty state: an illustration, a line that says what belongs here,
 * and an optional way out of the emptiness.
 *
 * Every screen in this app used to end at a grey sentence — "No groups in this
 * membership yet." — floating in a card that had been sized for a grid. Read at
 * a glance, that is indistinguishable from a page that failed to load. This
 * gives the state a shape: something to look at, a heading that names what is
 * missing, and a sentence that says why the space is empty and what fills it.
 *
 * The art is drawn INLINE and recoloured from the membership's brand colour,
 * exactly as LockIllustration is, and for the same reasons: a raster asset
 * cannot follow a creator's theme, and every membership here picks its own.
 * `accent` is the only colour input — everything else is the neutral paper the
 * art sits on, so a red brand and a teal brand both come out looking deliberate.
 *
 * Sizes: `compact` is for a state living inside a rail or a modal, where the
 * full-size art would push the card past the fold.
 */

/* Groups / people. Three member cards fanned out, the middle one lifted — a
   space that is ready for its first group rather than a broken grid. */
function GroupsArt({ accent, g }) {
    return (
        <>
            {/* the two behind, ghosted and tilted so the stack reads as depth */}
            <g opacity="0.9">
                <g transform="rotate(-9 62 104)">
                    <rect x="30" y="70" width="66" height="76" rx="14" fill={`url(#${g("paper")})`} stroke="#e6ebf2" strokeWidth="1.5" />
                    <circle cx="63" cy="96" r="13" fill="#eef2f7" />
                    <rect x="44" y="118" width="38" height="6" rx="3" fill="#e6ebf2" />
                    <rect x="52" y="130" width="22" height="6" rx="3" fill="#f1f5f9" />
                </g>
                <g transform="rotate(9 178 104)">
                    <rect x="144" y="70" width="66" height="76" rx="14" fill={`url(#${g("paper")})`} stroke="#e6ebf2" strokeWidth="1.5" />
                    <circle cx="177" cy="96" r="13" fill="#eef2f7" />
                    <rect x="158" y="118" width="38" height="6" rx="3" fill="#e6ebf2" />
                    <rect x="166" y="130" width="22" height="6" rx="3" fill="#f1f5f9" />
                </g>
            </g>

            {/* the card in front — the only one carrying brand colour, so the eye
                lands on it first */}
            <g>
                <rect x="84" y="52" width="72" height="96" rx="16" fill="#ffffff" stroke="#e6ebf2" strokeWidth="1.5" />
                <path d="M84 68a16 16 0 0 1 16-16h40a16 16 0 0 1 16 16v10H84z" fill={`url(#${g("brand")})`} />
                {/* an avatar cluster: three heads, the front one in brand colour */}
                <circle cx="106" cy="96" r="12" fill="#eef2f7" stroke="#ffffff" strokeWidth="3" />
                <circle cx="134" cy="96" r="12" fill="#e2e8f0" stroke="#ffffff" strokeWidth="3" />
                <circle cx="120" cy="98" r="14" fill={accent} fillOpacity="0.16" stroke="#ffffff" strokeWidth="3" />
                <circle cx="120" cy="94" r="4.6" fill={accent} fillOpacity="0.75" />
                <path d="M112.4 106a8 8 0 0 1 15.2 0z" fill={accent} fillOpacity="0.75" />
                <rect x="98" y="122" width="44" height="6" rx="3" fill="#e6ebf2" />
                <rect x="106" y="134" width="28" height="6" rx="3" fill="#f1f5f9" />
            </g>

            {/* the invitation: a plus badge, the one saturated mark in the frame */}
            <g transform="translate(158 132)">
                <circle cx="0" cy="0" r="19" fill="#ffffff" />
                <circle cx="0" cy="0" r="15" fill={accent} />
                <path d="M0-7v14M-7 0h14" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
            </g>
        </>
    );
}

/* A search that came back with nothing — the same paper, a lens over it. */
function SearchArt({ accent, g }) {
    return (
        <>
            <g opacity="0.95">
                <rect x="62" y="54" width="116" height="92" rx="16" fill={`url(#${g("paper")})`} stroke="#e6ebf2" strokeWidth="1.5" />
                <rect x="78" y="74" width="52" height="7" rx="3.5" fill="#e6ebf2" />
                <rect x="78" y="92" width="84" height="7" rx="3.5" fill="#eef2f7" />
                <rect x="78" y="110" width="36" height="7" rx="3.5" fill="#f1f5f9" />
            </g>
            <g transform="translate(146 116)">
                <circle cx="0" cy="0" r="30" fill="#ffffff" fillOpacity="0.92" />
                <circle cx="0" cy="0" r="24" fill={accent} fillOpacity="0.10" stroke={accent} strokeWidth="4" />
                <path d="M17 17l16 16" stroke={accent} strokeWidth="7" strokeLinecap="round" />
            </g>
        </>
    );
}

/* Events. A calendar with one day claimed, a ghost agenda card behind it — the
   page is waiting for a date, not broken. */
function EventsArt({ accent, g }) {
    return (
        <>
            {/* the agenda sheet behind, tilted, so the calendar has something to
                sit against */}
            <g transform="rotate(-8 92 106)" opacity="0.9">
                <rect x="54" y="66" width="70" height="82" rx="14" fill={`url(#${g("paper")})`} stroke="#e6ebf2" strokeWidth="1.5" />
                <rect x="66" y="84" width="40" height="6" rx="3" fill="#e6ebf2" />
                <rect x="66" y="98" width="30" height="6" rx="3" fill="#eef2f7" />
                <rect x="66" y="112" width="36" height="6" rx="3" fill="#eef2f7" />
            </g>

            {/* the calendar itself */}
            <g>
                <rect x="86" y="48" width="98" height="100" rx="16" fill="#ffffff" stroke="#e6ebf2" strokeWidth="1.5" />
                <path d="M86 64a16 16 0 0 1 16-16h66a16 16 0 0 1 16 16v10H86z" fill={`url(#${g("brand")})`} />
                {/* binder rings, drawn through the header so it reads as a wall calendar */}
                <rect x="106" y="38" width="7" height="20" rx="3.5" fill="#cbd5e1" />
                <rect x="157" y="38" width="7" height="20" rx="3.5" fill="#cbd5e1" />
                {/* the month grid: three rows of days */}
                <g fill="#e6ebf2">
                    <rect x="98" y="88" width="14" height="10" rx="3" />
                    <rect x="118" y="88" width="14" height="10" rx="3" />
                    <rect x="138" y="88" width="14" height="10" rx="3" />
                    <rect x="158" y="88" width="14" height="10" rx="3" />
                    <rect x="98" y="106" width="14" height="10" rx="3" />
                    <rect x="158" y="106" width="14" height="10" rx="3" />
                    <rect x="98" y="124" width="14" height="10" rx="3" fill="#eef2f7" />
                    <rect x="118" y="124" width="14" height="10" rx="3" fill="#eef2f7" />
                    <rect x="138" y="124" width="14" height="10" rx="3" fill="#eef2f7" />
                </g>
                {/* the day that is spoken for */}
                <rect x="116" y="103" width="38" height="16" rx="8" fill={accent} fillOpacity="0.16" />
                <rect x="118" y="106" width="14" height="10" rx="3" fill={accent} />
            </g>

            {/* a clock badge — the "when", not just the "what" */}
            <g transform="translate(178 128)">
                <circle cx="0" cy="0" r="19" fill="#ffffff" />
                <circle cx="0" cy="0" r="15" fill={accent} />
                <path d="M0-8v8l6 4" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </g>
        </>
    );
}

/* Members. A roster: rows of faces waiting for names. */
function MembersArt({ accent, g }) {
    return (
        <>
            <rect x="58" y="46" width="124" height="106" rx="18" fill={`url(#${g("paper")})`} stroke="#e6ebf2" strokeWidth="1.5" />
            {/* the first row is the one that exists — brand avatar, fuller bars */}
            <g>
                <circle cx="86" cy="72" r="12" fill={accent} fillOpacity="0.16" />
                <circle cx="86" cy="69" r="4.2" fill={accent} fillOpacity="0.8" />
                <path d="M79 80a7.5 7.5 0 0 1 14 0z" fill={accent} fillOpacity="0.8" />
                <rect x="106" y="66" width="52" height="6" rx="3" fill="#dfe6ee" />
                <rect x="106" y="77" width="32" height="5" rx="2.5" fill="#eef2f7" />
            </g>
            <g opacity="0.75">
                <circle cx="86" cy="104" r="12" fill="#eef2f7" />
                <rect x="106" y="98" width="44" height="6" rx="3" fill="#e6ebf2" />
                <rect x="106" y="109" width="26" height="5" rx="2.5" fill="#f1f5f9" />
                <circle cx="86" cy="134" r="12" fill="#f1f5f9" />
                <rect x="106" y="128" width="36" height="6" rx="3" fill="#eef2f7" />
            </g>
            <g transform="translate(172 132)">
                <circle cx="0" cy="0" r="19" fill="#ffffff" />
                <circle cx="0" cy="0" r="15" fill={accent} />
                <path d="M0-7v14M-7 0h14" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
            </g>
        </>
    );
}

/* Leaderboard. An empty podium — the ranking exists, nobody has climbed it. */
function LeaderboardArt({ accent, g }) {
    return (
        <>
            {/* trophy above the winner's step */}
            <g transform="translate(120 62)">
                <path d="M-13-14h26v9a13 13 0 0 1-26 0z" fill={`url(#${g("brand")})`} />
                <path d="M-13-11h-7v3a8 8 0 0 0 8 8M13-11h7v3a8 8 0 0 1-8 8" stroke={accent} strokeOpacity="0.55" strokeWidth="2.6" strokeLinecap="round" />
                <rect x="-3.5" y="-5" width="7" height="12" rx="2" fill={accent} fillOpacity="0.55" />
                <rect x="-11" y="7" width="22" height="5" rx="2.5" fill={accent} fillOpacity="0.75" />
            </g>
            {/* the three steps */}
            <g>
                <rect x="48" y="112" width="44" height="38" rx="8" fill={`url(#${g("paper")})`} stroke="#e6ebf2" strokeWidth="1.5" />
                <rect x="98" y="88" width="44" height="62" rx="8" fill="#ffffff" stroke="#e6ebf2" strokeWidth="1.5" />
                <rect x="98" y="88" width="44" height="14" rx="7" fill={`url(#${g("brand")})`} />
                <rect x="148" y="122" width="44" height="28" rx="8" fill={`url(#${g("paper")})`} stroke="#e6ebf2" strokeWidth="1.5" />
                {/* rank marks */}
                <rect x="62" y="126" width="16" height="6" rx="3" fill="#e6ebf2" />
                <rect x="112" y="112" width="16" height="6" rx="3" fill="#dfe6ee" />
                <rect x="162" y="132" width="16" height="6" rx="3" fill="#eef2f7" />
            </g>
        </>
    );
}

/* Badges. A rosette with nothing engraved on it yet, two ghosts behind. */
function BadgesArt({ accent, g }) {
    return (
        <>
            <g opacity="0.85">
                <circle cx="72" cy="104" r="26" fill={`url(#${g("paper")})`} stroke="#e6ebf2" strokeWidth="1.5" />
                <circle cx="168" cy="104" r="26" fill={`url(#${g("paper")})`} stroke="#e6ebf2" strokeWidth="1.5" />
                <circle cx="72" cy="104" r="13" fill="#eef2f7" />
                <circle cx="168" cy="104" r="13" fill="#eef2f7" />
            </g>
            {/* ribbon tails */}
            <path d="M104 120l-8 34 16-8 8 8 8-8 16 8-8-34z" fill={accent} fillOpacity="0.22" />
            {/* the medal */}
            <circle cx="120" cy="96" r="38" fill="#ffffff" stroke="#e6ebf2" strokeWidth="1.5" />
            <circle cx="120" cy="96" r="30" fill={`url(#${g("brand")})`} />
            <circle cx="120" cy="96" r="21" fill="#ffffff" fillOpacity="0.22" />
            {/* a check, not a star: the admin's badge section is named with
                Heroicons' check-badge, so both ends of the product agree on what
                a badge looks like */}
            <path d="M107 96.5l9 9 18-19" stroke="#ffffff" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </>
    );
}

/* Plans. A subscription card, unclaimed. */
function PlansArt({ accent, g }) {
    return (
        <>
            <g transform="rotate(-7 96 104)" opacity="0.9">
                <rect x="54" y="66" width="92" height="72" rx="14" fill={`url(#${g("paper")})`} stroke="#e6ebf2" strokeWidth="1.5" />
            </g>
            <rect x="76" y="52" width="106" height="86" rx="16" fill="#ffffff" stroke="#e6ebf2" strokeWidth="1.5" />
            <path d="M76 68a16 16 0 0 1 16-16h74a16 16 0 0 1 16 16v12H76z" fill={`url(#${g("brand")})`} />
            <rect x="92" y="92" width="30" height="10" rx="5" fill={accent} fillOpacity="0.22" />
            <rect x="92" y="110" width="62" height="6" rx="3" fill="#e6ebf2" />
            <rect x="92" y="122" width="40" height="6" rx="3" fill="#f1f5f9" />
            <g transform="translate(170 122)">
                <circle cx="0" cy="0" r="19" fill="#ffffff" />
                <circle cx="0" cy="0" r="15" fill={accent} />
                <path d="M-6 0.4l4.2 4.2L6.6-4" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </g>
        </>
    );
}

/* Map. A patch of world with one pin dropped and no one on it yet. */
function MapArt({ accent, g }) {
    return (
        <>
            <rect x="58" y="52" width="124" height="96" rx="18" fill={`url(#${g("paper")})`} stroke="#e6ebf2" strokeWidth="1.5" />
            {/* landmasses + a route, ghosted */}
            <path d="M72 128c14-10 20 4 34-6s18-22 34-18 22 16 34 10" stroke="#e6ebf2" strokeWidth="6" strokeLinecap="round" fill="none" />
            <path d="M74 84c12 6 18-6 30-2s14 14 26 10" stroke="#eef2f7" strokeWidth="6" strokeLinecap="round" fill="none" />
            <circle cx="86" cy="118" r="4" fill="#e2e8f0" />
            <circle cx="158" cy="88" r="4" fill="#e2e8f0" />
            {/* the pin */}
            <g transform="translate(120 74)">
                <ellipse cx="0" cy="46" rx="14" ry="4.5" fill={accent} fillOpacity="0.18" />
                <path d="M0 44c0 0-18-19-18-30a18 18 0 0 1 36 0c0 11-18 30-18 30z" fill={`url(#${g("brand")})`} />
                <circle cx="0" cy="14" r="7" fill="#ffffff" />
            </g>
        </>
    );
}

/* Rewards. A gift, still tied — the perk exists as an idea, not yet as a thing
   you can claim. */
function RewardsArt({ accent, g }) {
    return (
        <>
            {/* two ghost boxes behind, so it reads as a shelf of perks */}
            <g opacity="0.85">
                <rect x="42" y="96" width="52" height="48" rx="12" fill={`url(#${g("paper")})`} stroke="#e6ebf2" strokeWidth="1.5" />
                <rect x="146" y="96" width="52" height="48" rx="12" fill={`url(#${g("paper")})`} stroke="#e6ebf2" strokeWidth="1.5" />
            </g>
            {/* the gift */}
            <g>
                <rect x="80" y="86" width="80" height="62" rx="12" fill="#ffffff" stroke="#e6ebf2" strokeWidth="1.5" />
                <rect x="74" y="72" width="92" height="24" rx="9" fill={`url(#${g("brand")})`} />
                {/* ribbon down the middle */}
                <rect x="113" y="72" width="14" height="76" fill={accent} fillOpacity="0.22" />
                {/* bow */}
                <path d="M120 72c-6-4-18-12-18-20a9 9 0 0 1 18 6 9 9 0 0 1 18-6c0 8-12 16-18 20z" fill={accent} />
                <circle cx="120" cy="60" r="5" fill="#ffffff" fillOpacity="0.9" />
            </g>
            {/* a star badge — a perk worth earning */}
            <g transform="translate(168 130)">
                <circle cx="0" cy="0" r="18" fill="#ffffff" />
                <circle cx="0" cy="0" r="14" fill={accent} />
                <path d="M0-7l2.1 4.3 4.7.7-3.4 3.3.8 4.7L0 3.8-4.2 6l.8-4.7-3.4-3.3 4.7-.7z" fill="#ffffff" />
            </g>
        </>
    );
}

/* Courses. A lesson card with a play button, two more stacked behind it. */
function CoursesArt({ accent, g }) {
    return (
        <>
            <g opacity="0.9">
                <rect x="66" y="40" width="108" height="20" rx="10" fill={`url(#${g("paper")})`} stroke="#e6ebf2" strokeWidth="1.5" />
                <rect x="58" y="52" width="124" height="22" rx="11" fill="#ffffff" stroke="#e6ebf2" strokeWidth="1.5" />
            </g>
            {/* the course card in front */}
            <rect x="50" y="66" width="140" height="86" rx="16" fill="#ffffff" stroke="#e6ebf2" strokeWidth="1.5" />
            {/* its thumbnail, brand-filled, with a play head */}
            <rect x="62" y="78" width="60" height="46" rx="10" fill={`url(#${g("brand")})`} />
            <path d="M86 92l16 9-16 9z" fill="#ffffff" fillOpacity="0.95" />
            <rect x="132" y="82" width="46" height="7" rx="3.5" fill="#dfe6ee" />
            <rect x="132" y="96" width="34" height="6" rx="3" fill="#eef2f7" />
            {/* progress rail, barely started */}
            <rect x="132" y="112" width="46" height="6" rx="3" fill="#eef2f7" />
            <rect x="132" y="112" width="14" height="6" rx="3" fill={accent} fillOpacity="0.7" />
            <rect x="62" y="134" width="76" height="6" rx="3" fill="#f1f5f9" />
            {/* graduation badge */}
            <g transform="translate(174 128)">
                <circle cx="0" cy="0" r="19" fill="#ffffff" />
                <circle cx="0" cy="0" r="15" fill={accent} />
                <path d="M-8-2l8-4 8 4-8 4z" fill="#ffffff" />
                <path d="M-4 0.6V5c0 1.6 8 1.6 8 0V0.6" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" fill="none" />
            </g>
        </>
    );
}

const ART = { groups: GroupsArt, search: SearchArt, events: EventsArt, members: MembersArt, leaderboard: LeaderboardArt, badges: BadgesArt, plans: PlansArt, map: MapArt, rewards: RewardsArt, courses: CoursesArt };

export function EmptyIllustration({ art = "groups", accent = "#2CCD4F", className = "" }) {
    // Gradient ids have to be unique per instance: two of these mounted at once
    // sharing an id makes the second render with the first one's fill.
    const uid = useId().replace(/:/g, "");
    const g = (name) => `${name}-${uid}`;
    const Art = ART[art] || GroupsArt;

    return (
        <svg viewBox="0 0 240 180" className={className} fill="none" role="presentation" aria-hidden="true">
            <defs>
                <linearGradient id={g("brand")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={accent} stopOpacity="0.9" />
                    <stop offset="100%" stopColor={accent} stopOpacity="0.55" />
                </linearGradient>
                <linearGradient id={g("paper")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#ffffff" />
                    <stop offset="100%" stopColor="#f2f5f9" />
                </linearGradient>
                <radialGradient id={g("aura")} cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={accent} stopOpacity="0.26" />
                    <stop offset="70%" stopColor={accent} stopOpacity="0.05" />
                    <stop offset="100%" stopColor={accent} stopOpacity="0" />
                </radialGradient>
            </defs>

            {/* brand aura + dotted orbit: the same framing LockIllustration uses,
                so the two read as one family */}
            <circle cx="120" cy="100" r="82" fill={`url(#${g("aura")})`} />
            <circle cx="120" cy="100" r="74" stroke={accent} strokeOpacity="0.22" strokeWidth="1.5" strokeDasharray="2 9" strokeLinecap="round" />

            <Art accent={accent} g={g} />

            {/* sparkles — the last 5% that makes it look drawn rather than diagrammed */}
            <path d="M52 40l2.6 6.6L61 49l-6.4 2.6L52 58l-2.6-6.4L43 49l6.4-2.4z" fill={accent} fillOpacity="0.6" />
            <path d="M196 52l1.7 4.3L202 58l-3.9 1.7L196 64l-1.7-4.3L190 58l4.3-1.7z" fill={accent} fillOpacity="0.4" />
        </svg>
    );
}

export default function EmptyState({
    art = "groups",
    accent = "#2CCD4F",
    title,
    message,
    action = null,          // { label, onClick } — optional
    secondary = null,       // { label, onClick } — optional, quieter
    compact = false,
    // Vertical room. Defaults by size; pass a Tailwind padding class when the
    // state sits somewhere tight (a dropdown, a rail card) — overriding via
    // className would leave two py-* classes fighting, and the winner would be
    // whichever the stylesheet happened to emit last, not the one written here.
    padding = null,
    className = "",
}) {
    return (
        <div className={`flex flex-col items-center justify-center px-6 text-center ${padding || (compact ? "py-10" : "py-14 sm:py-20")} ${className}`}>
            <EmptyIllustration art={art} accent={accent} className={compact ? "w-40 max-w-full" : "w-56 max-w-full sm:w-64"} />

            {title ? (
                <h3 className={`mt-6 font-extrabold tracking-tight text-gray-900 ${compact ? "text-base" : "text-lg sm:text-xl"}`}>
                    {title}
                </h3>
            ) : null}

            {message ? (
                <p className={`mt-2 max-w-sm text-gray-500 ${compact ? "text-[13px] leading-relaxed" : "text-sm leading-relaxed"}`}>
                    {message}
                </p>
            ) : null}

            {action || secondary ? (
                <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
                    {action ? (
                        <button
                            type="button"
                            onClick={action.onClick}
                            style={{ backgroundColor: accent, color: "var(--brand-btn-text, #fff)" }}
                            className="rounded-full px-6 py-2.5 text-sm font-bold shadow-md transition hover:opacity-90"
                        >
                            {action.label}
                        </button>
                    ) : null}
                    {secondary ? (
                        <button
                            type="button"
                            onClick={secondary.onClick}
                            className="rounded-full border border-gray-200 bg-surface px-6 py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition hover:border-gray-300 hover:text-gray-800"
                        >
                            {secondary.label}
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
